/**
 * StringEditCell - Edit cell for simple string values
 *
 * Usage:
 * - Text fields (name, website, etc.)
 * - URL fields
 * - Email fields
 */

import { InputBase } from '@mui/material'
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium'
import React, { useCallback, useLayoutEffect, useRef } from 'react'

interface StringEditCellProps extends GridRenderEditCellParams {
  placeholder?: string
  inputType?: 'text' | 'url' | 'email' | 'tel'
}

export const StringEditCell: React.FC<StringEditCellProps> = ({
  id,
  field,
  value,
  placeholder,
  inputType = 'text',
}) => {
  const apiRef = useGridApiContext()
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus input on mount
  useLayoutEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      apiRef.current.setEditCellValue({ id, field, value: event.target.value })
    },
    [apiRef, id, field]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true })
      }
    },
    [apiRef, id, field]
  )

  return (
    <InputBase
      inputRef={inputRef}
      value={value ?? ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      type={inputType}
      fullWidth
      sx={{
        px: 1,
        fontSize: 'inherit',
        '& input': {
          padding: 0,
        },
      }}
    />
  )
}

export default StringEditCell
